<template>
    <span class="form-checkbox" :class="{toggle}" v-bind="$attrs">
        <input type="hidden" :name="name" :value="valueOff" v-if="showHidden"/>
        <label>
            <input type="checkbox" :name="name" v-model="checked" :value="valueOn" v-on="events" v-bind="$attrs" :id="_id" :disabled="!!disabled"/>
            <i class="material-icons form-checkbox-icon pointer" aria-hidden="true" :title="title!" role="img"></i>
            <span v-if="label" v-html="label"/>
            <span v-else class="hidden">Auswahl Option</span>
            <slot></slot>
        </label>
    </span>
</template>

<script setup lang="ts">
    import {useAbstractFormInput} from '@/composables/abstractFormInput';
    import {usePortuna} from '@/composables/portuna';
    import {FormRowKey} from '@/library/form';
    import {ref, computed, toRefs, inject} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'update:modelValue', moduleValue: InputValueTypes): void;
    }>();

    const props = withDefaults(
        defineProps<{
            id?: string;
            name?: string;
            required?: boolean;
            overwriteRequired?: boolean;
            errorName?: string;
            filterName?: string;
            modelValue: InputValueTypes;
            valueOff?: number | string | boolean;
            valueOn?: number | string | boolean;
            showHidden?: boolean;
            title?: Nullable<string>;
            disabled?: boolean | number;
            toggle?: boolean;
            label?: string;
            events?: object;
        }>(),
        {
            events: () => ({}),
            required: false,
            overwriteRequired: false,
            errorName: '',
            filterName: '',
            valueOff: 0,
            valueOn: 1,
            showHidden: true,
            title: null,
            disabled: false,
            toggle: false,
            label: '',
        },
    );

    const {
              id,
              name,
              required,
              overwriteRequired,
              errorName,
              filterName,
              modelValue,
              valueOff,
              valueOn,
              showHidden,
              title,
              disabled,
              toggle,
              label,
              events,
          } = toRefs(props);

    const parentFormRow = inject<Nullable<IFormRowContext>>(FormRowKey, null);

    const {
              _id,
              validateValue,
              expose,
          } = useAbstractFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
    );

    const input = ref<InputValueTypes>();

    const checked = computed({
        get: (): number | string | boolean =>
        {
            return (
                true === modelValue.value ||
                modelValue.value === valueOn.value ||
                (Array.isArray(modelValue.value) && modelValue.value.includes(valueOn.value))
            );
        },
        set: (newValue: number | string | boolean) =>
        {
            input.value = newValue ? valueOn.value : valueOff.value;

            if (!Array.isArray(modelValue.value))
            {
                emit('update:modelValue', input.value);

                validateValue(input.value); // @todo: check if correct
            }
            else
            {
                let value: Array<InputValueType> = Object.assign([], modelValue.value);

                if (newValue)
                {
                    value.push(valueOn.value);
                }
                else
                {
                    value.splice(value.indexOf(valueOn.value), 1);
                }

                emit('update:modelValue', value);

                validateValue(value);
            }
        },
    });

    input.value = checked.value ? valueOn.value : valueOff.value;

    defineExpose(Object.assign({}, portuna, expose, {
        componentName: 'FormInputCheckbox',
    }));
</script>